import { Pause, Play } from "lucide-react";
import { memo, useEffect, useRef } from "react";
import { Visualizer, type VisualizerProps } from "react-sound-visualizer";

interface RecorderVisualizerProps {
  audio: MediaStream | null;
  paused: boolean;
  controls?: boolean;
  onToggle?: () => void;
  mode?: "continuous" | "current";
}

function SafeVisualizer(props: VisualizerProps): JSX.Element {
  const stopRef = useRef<() => void>();

  useEffect(() => {
    return (): void => {
      stopRef.current?.();
      stopRef.current = undefined;
    };
  }, []);

  return (
    <Visualizer {...props}>
      {({ canvasRef, stop }) => {
        if (stopRef.current && stopRef.current !== stop) {
          stopRef.current();
        }
        stopRef.current = stop;

        return props.children ? (
          typeof props.children === "function" ? (
            props.children({ canvasRef, stop })
          ) : (
            props.children
          )
        ) : (
          <canvas ref={canvasRef} />
        );
      }}
    </Visualizer>
  );
}

const RecorderVisualizer = ({
  audio,
  paused,
  controls = false,
  onToggle,
  mode = "continuous",
}: RecorderVisualizerProps): JSX.Element => {
  const emptyVisualizer = (
    <div className="w-full h-[52px] min-h-[52px] bg-primary rounded-xl flex items-center justify-center" />
  );

  if (!controls && (!audio || audio.getAudioTracks().length === 0)) {
    return emptyVisualizer;
  }

  if (!controls) {
    if (paused) {
      return emptyVisualizer;
    }

    return (
      <div className="w-full h-[52px] min-h-[52px] bg-primary rounded-xl flex items-center justify-center">
        <SafeVisualizer
          key={`${paused}-${mode}-${!!audio}`}
          autoStart
          audio={audio}
          heightNorm={0.8}
          mode={"current"}
          rectWidth={"thick"}
          strokeColor={"#ffffff"}
        >
          {({ canvasRef }) => (
            <canvas ref={canvasRef} className="w-[90%] h-[50%] rounded-xl" />
          )}
        </SafeVisualizer>
      </div>
    );
  }

  return (
    <div className="w-full h-[52px] min-h-[52px] bg-primary rounded-xl flex items-center">
      <div className="w-1/5 flex justify-center items-center">
        <button
          className="bg-transparent text-white px-2 py-1 rounded"
          onClick={onToggle}
        >
          {paused ? <Play fill="#fff" /> : <Pause fill="#fff" />}
        </button>
      </div>
      <div className="w-3/5 h-1/2 flex justify-center items-center">
        {audio && audio.getAudioTracks().length > 0 ? (
          <SafeVisualizer
            key={`${paused}-${mode}-${!!audio}`}
            autoStart
            audio={audio}
            barRadius={0.1}
            heightNorm={0.2}
            mode={mode}
            rectWidth={"thick"}
            slices={50}
            strokeColor={"#ffffff"}
          >
            {({ canvasRef }) => (
              <canvas ref={canvasRef} className="w-full h-full rounded-xl" />
            )}
          </SafeVisualizer>
        ) : (
          <div className="w-3/5 h-1/2" />
        )}
      </div>
    </div>
  );
};

export default memo(RecorderVisualizer);
